import React, { useState, useEffect, useMemo } from 'react';
import { apiClient } from '../../utils/apiClient';
import {
    Megaphone,
    Calendar,
    Users,
    Building2,
    Shield,
    Pin,
    LayoutDashboard,
    ExternalLink,
    Search,
    Check,
    Eye,
    AlertCircle,
    Sparkles,
    Clock
} from 'lucide-react';

const PRIORITY_OPTIONS = [
    { value: 0, label: 'Normal (Standart)', badgeCls: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
    { value: 10, label: 'Yüksek Öncelikli', badgeCls: 'bg-amber-500/15 text-amber-800 dark:text-amber-300' },
    { value: 20, label: 'Kritik / Acil Duyuru', badgeCls: 'bg-rose-500/15 text-rose-700 dark:text-rose-300' },
];

const STATUS_OPTIONS = [
    { value: 1, label: '🟢 Yayında (Aktif)', desc: 'Yayın tarih aralığında hedef kitleye gösterilir ve bildirim gönderilir.' },
    { value: 0, label: '📝 Taslak Olarak Kaydet', desc: 'Yalnızca yöneticiler görebilir, personellere bildirim gitmez.' },
    { value: 2, label: '⏸️ Pasif (Arşiv)', desc: 'Yayından kaldırılır, panoda ve mobilde gösterilmez.' },
];

const TARGET_OPTIONS = [
    { value: 1, label: 'Tüm Firma', icon: Users, desc: 'Tüm aktif personellere gösterilir' },
    { value: 2, label: 'Seçili Departmanlar', icon: Building2, desc: 'Sadece seçilen departmanlardaki personeller' },
    { value: 3, label: 'Seçili Roller', icon: Shield, desc: 'Sadece seçilen rollere sahip kullanıcılar' },
];

const formatLocalForInput = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return '';
    const pad = (n) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const AnnouncementForm = ({ announcement, onSubmit, onCancel, isNew, submitting }) => {
    const [formData, setFormData] = useState({
        title: '',
        summary: '',
        content: '',
        isActive: true,
        status: 1, // 0: Draft, 1: Published, 2: Passive
        publishStartAt: '',
        publishEndAt: '',
        priority: 0,
        pinned: false,
        showOnDashboard: true,
        targetType: 1, // 1: AllCompany, 2: Departments, 3: Roles
        departmentIds: [],
        roleIds: [],
        actionButtonText: '',
        actionUrl: ''
    });

    const [departments, setDepartments] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loadingTargets, setLoadingTargets] = useState(false);
    const [targetSearch, setTargetSearch] = useState('');
    const [showPreview, setShowPreview] = useState(false);
    const [localError, setLocalError] = useState(null);

    useEffect(() => {
        let mounted = true;
        const fetchDepsAndRoles = async () => {
            setLoadingTargets(true);
            try {
                const [depsRes, rolesRes] = await Promise.allSettled([
                    apiClient('Department'),
                    apiClient('roles')
                ]);
                if (!mounted) return;
                if (depsRes.status === 'fulfilled') {
                    const list = Array.isArray(depsRes.value) ? depsRes.value : depsRes.value?.items || [];
                    setDepartments(list);
                }
                if (rolesRes.status === 'fulfilled') {
                    const list = Array.isArray(rolesRes.value) ? rolesRes.value : rolesRes.value?.items || [];
                    setRoles(list);
                }
            } finally {
                if (mounted) setLoadingTargets(false);
            }
        };
        fetchDepsAndRoles();
        return () => { mounted = false; };
    }, []);

    useEffect(() => {
        const statusMap = { Draft: 0, Published: 1, Passive: 2 };
        const targetTypeMap = { AllCompany: 1, Departments: 2, Roles: 3 };

        if (announcement) {
            let statusVal = announcement.status;
            if (statusVal === undefined) statusVal = 1;
            if (typeof statusVal === 'string') statusVal = statusMap[statusVal] ?? 1;
            if (!announcement.isActive && statusVal === 1) statusVal = 2;

            let targetTypeVal = announcement.targetType;
            if (targetTypeVal === undefined) targetTypeVal = 1;
            if (typeof targetTypeVal === 'string') targetTypeVal = targetTypeMap[targetTypeVal] ?? 1;

            setFormData({
                title: announcement.title || '',
                summary: announcement.summary || '',
                content: announcement.content || '',
                isActive: statusVal === 1,
                status: statusVal,
                publishStartAt: formatLocalForInput(announcement.publishStartAt),
                publishEndAt: formatLocalForInput(announcement.publishEndAt),
                priority: announcement.priority ?? 0,
                pinned: Boolean(announcement.pinned),
                showOnDashboard: announcement.showOnDashboard !== undefined ? announcement.showOnDashboard : true,
                targetType: targetTypeVal,
                departmentIds: Array.isArray(announcement.departmentIds) ? announcement.departmentIds.map(Number) : [],
                roleIds: Array.isArray(announcement.roleIds) ? announcement.roleIds.map(Number) : [],
                actionButtonText: announcement.actionButtonText || '',
                actionUrl: announcement.actionUrl || ''
            });
        }
    }, [announcement]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => {
            const next = {
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            };
            if (name === 'status') {
                const numStatus = parseInt(value, 10);
                next.status = numStatus;
                next.isActive = numStatus === 1;
            }
            return next;
        });
    };

    const toggleTargetItem = (listKey, id) => {
        const numId = Number(id);
        setFormData((prev) => {
            const current = prev[listKey] || [];
            const exists = current.includes(numId);
            return {
                ...prev,
                [listKey]: exists ? current.filter((x) => x !== numId) : [...current, numId]
            };
        });
    };

    const setQuickEndDate = (days) => {
        const base = formData.publishStartAt ? new Date(formData.publishStartAt) : new Date();
        if (isNaN(base.getTime())) return;
        base.setDate(base.getDate() + days);
        setFormData((prev) => ({
            ...prev,
            publishEndAt: formatLocalForInput(base)
        }));
    };

    const filteredDepartments = useMemo(() => {
        if (!targetSearch.trim()) return departments;
        const q = targetSearch.trim().toLocaleLowerCase('tr-TR');
        return departments.filter((d) => String(d.name || '').toLocaleLowerCase('tr-TR').includes(q));
    }, [departments, targetSearch]);

    const filteredRoles = useMemo(() => {
        if (!targetSearch.trim()) return roles;
        const q = targetSearch.trim().toLocaleLowerCase('tr-TR');
        return roles.filter((r) => String(r.name || '').toLocaleLowerCase('tr-TR').includes(q));
    }, [roles, targetSearch]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLocalError(null);

        const statusNum = parseInt(formData.status, 10);
        const targetTypeNum = parseInt(formData.targetType, 10);
        const priorityNum = Math.max(0, parseInt(formData.priority, 10) || 0);

        if (!formData.title.trim()) {
            setLocalError('Lütfen duyuru başlığını girin.');
            return;
        }
        if (!formData.content.trim()) {
            setLocalError('Lütfen duyuru içeriğini girin.');
            return;
        }
        if (targetTypeNum === 2 && (!formData.departmentIds || formData.departmentIds.length === 0)) {
            setLocalError('Hedef kitle "Seçili Departmanlar" olduğunda en az bir departman seçmelisiniz.');
            return;
        }
        if (targetTypeNum === 3 && (!formData.roleIds || formData.roleIds.length === 0)) {
            setLocalError('Hedef kitle "Seçili Roller" olduğunda en az bir rol seçmelisiniz.');
            return;
        }
        if (formData.publishStartAt && formData.publishEndAt && new Date(formData.publishEndAt) < new Date(formData.publishStartAt)) {
            setLocalError('Yayın bitiş tarihi, başlangıç tarihinden önce olamaz.');
            return;
        }

        const submissionData = {
            ...formData,
            title: formData.title.trim(),
            summary: formData.summary.trim() || null,
            content: formData.content.trim(),
            status: statusNum,
            isActive: statusNum === 1,
            priority: priorityNum,
            targetType: targetTypeNum,
            publishStartAt: formData.publishStartAt || null,
            publishEndAt: formData.publishEndAt || null,
            actionButtonText: formData.actionButtonText.trim() || null,
            actionUrl: formData.actionUrl.trim() || null,
        };

        if (announcement?.id) {
            submissionData.id = announcement.id;
        }

        try {
            await onSubmit(submissionData);
        } catch (err) {
            setLocalError(err.message || 'Duyuru kaydedilirken bir hata oluştu.');
        }
    };

    const targetTypeNum = parseInt(formData.targetType, 10);

    return (
        <form onSubmit={handleSubmit} className="space-y-5">
            {localError && (
                <div className="flex items-start gap-2.5 rounded-xl border border-rose-200 bg-rose-50/90 p-3.5 text-xs font-semibold text-rose-700 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-300">
                    <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                    <span>{localError}</span>
                </div>
            )}

            {/* Üst Sekme: Düzenleme vs Canlı Önizleme */}
            <div className="flex items-center justify-between border-b border-slate-200 pb-3 dark:border-slate-800">
                <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 dark:text-slate-400">
                    <Megaphone className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                    <span>{isNew ? 'Yeni kurumsal duyuru oluşturun' : `Duyuru #${announcement?.id} düzenleniyor`}</span>
                </div>
                <button
                    type="button"
                    onClick={() => setShowPreview((p) => !p)}
                    className={`inline-flex items-center gap-1.5 rounded-xl border px-3 py-1.5 text-xs font-bold transition ${
                        showPreview
                            ? 'border-indigo-600 bg-indigo-600 text-white'
                            : 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200'
                    }`}
                >
                    <Eye className="h-3.5 w-3.5" />
                    {showPreview ? 'Forma Dön' : 'Canlı Önizleme'}
                </button>
            </div>

            {showPreview ? (
                <div className="space-y-4 rounded-2xl border border-slate-200 bg-slate-50/70 p-5 dark:border-slate-800 dark:bg-slate-900/60">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                        Dashboard & Mobil Kart Önizlemesi
                    </div>

                    <div
                        className={`rounded-xl border p-4 shadow-sm ${
                            formData.pinned
                                ? 'border-amber-200/90 bg-gradient-to-r from-amber-50/80 to-white dark:border-amber-800/60 dark:from-amber-950/30 dark:to-slate-900'
                                : 'border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900'
                        }`}
                    >
                        <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                                {formData.pinned && <Pin className="h-4 w-4 shrink-0 text-amber-500" />}
                                <h4 className="truncate text-sm font-bold text-slate-900 dark:text-white">
                                    {formData.title || 'Duyuru Başlığı'}
                                </h4>
                            </div>
                            <span className="shrink-0 text-[11px] font-medium text-slate-400">
                                {formData.publishStartAt
                                    ? new Date(formData.publishStartAt).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' })
                                    : 'Bugün'}
                            </span>
                        </div>
                        {formData.summary && (
                            <p className="mt-1.5 text-xs font-medium text-slate-600 dark:text-slate-300">
                                {formData.summary}
                            </p>
                        )}
                        <div className="mt-3 border-t border-slate-100 pt-3 text-xs leading-relaxed whitespace-pre-wrap text-slate-700 dark:border-slate-800 dark:text-slate-200">
                            {formData.content || 'Duyuru detaylı metni burada görüntülenecektir...'}
                        </div>
                        {formData.actionButtonText && formData.actionUrl && (
                            <div className="mt-3 flex justify-end">
                                <span className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-bold text-white">
                                    {formData.actionButtonText}
                                    <ExternalLink className="h-3.5 w-3.5" />
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <>
                    {/* Başlık ve Özet */}
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div className="space-y-1.5">
                            <div className="flex items-center justify-between">
                                <label className="text-xs font-bold text-slate-700 dark:text-slate-200">
                                    Duyuru Başlığı <span className="text-rose-500">*</span>
                                </label>
                                <span className="text-[10px] text-slate-400">{formData.title.length}/200</span>
                            </div>
                            <input
                                type="text"
                                name="title"
                                maxLength={200}
                                value={formData.title}
                                onChange={handleChange}
                                placeholder="Örn: Resmi Tatil Çalışma Düzeni Hakkında"
                                required
                                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-indigo-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                            />
                        </div>

                        <div className="space-y-1.5">
                            <div className="flex items-center justify-between">
                                <label className="text-xs font-bold text-slate-700 dark:text-slate-200">
                                    Kısa Özet (Liste ve Bildirim Alt Metni)
                                </label>
                                <span className="text-[10px] text-slate-400">{formData.summary.length}/500</span>
                            </div>
                            <input
                                type="text"
                                name="summary"
                                maxLength={500}
                                value={formData.summary}
                                onChange={handleChange}
                                placeholder="Boş bırakılırsa içerikten otomatik özetlenir..."
                                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-indigo-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                            />
                        </div>
                    </div>

                    {/* İçerik */}
                    <div className="space-y-1.5">
                        <label className="block text-xs font-bold text-slate-700 dark:text-slate-200">
                            Duyuru İçeriği <span className="text-rose-500">*</span>
                        </label>
                        <textarea
                            name="content"
                            value={formData.content}
                            onChange={handleChange}
                            required
                            rows={4}
                            placeholder="Duyurunun tam metnini buraya yazın..."
                            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm leading-relaxed text-slate-800 outline-none transition focus:border-indigo-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                        />
                    </div>

                    {/* Yayın Durumu ve Öncelik */}
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div className="space-y-1.5">
                            <label className="block text-xs font-bold text-slate-700 dark:text-slate-200">
                                Yayın Durumu
                            </label>
                            <select
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-800 outline-none transition focus:border-indigo-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                            >
                                {STATUS_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                            <p className="text-[11px] text-slate-500 dark:text-slate-400">
                                {STATUS_OPTIONS.find((x) => x.value === Number(formData.status))?.desc}
                            </p>
                        </div>

                        <div className="space-y-1.5">
                            <label className="block text-xs font-bold text-slate-700 dark:text-slate-200">
                                Önem / Öncelik Seviyesi
                            </label>
                            <div className="grid grid-cols-3 gap-2">
                                {PRIORITY_OPTIONS.map((p) => {
                                    const active = Number(formData.priority) === p.value;
                                    return (
                                        <button
                                            key={p.value}
                                            type="button"
                                            onClick={() => setFormData((prev) => ({ ...prev, priority: p.value }))}
                                            className={`rounded-xl border px-2.5 py-2 text-xs font-bold transition ${
                                                active
                                                    ? 'border-indigo-600 bg-indigo-50/90 text-indigo-700 ring-1 ring-indigo-600 dark:border-indigo-400 dark:bg-indigo-950/60 dark:text-indigo-300'
                                                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300'
                                            }`}
                                        >
                                            {p.label.split(' ')[0]}
                                        </button>
                                    );
                                })}
                            </div>
                            <p className="text-[11px] text-slate-500 dark:text-slate-400">
                                Yüksek öncelikli duyurular listede ve panoda üst sırada yer alır.
                            </p>
                        </div>
                    </div>

                    {/* Yayın Tarih Aralığı + Hızlı Seçim */}
                    <div className="rounded-2xl border border-slate-200/90 bg-slate-50/60 p-3.5 dark:border-slate-800 dark:bg-slate-900/50">
                        <div className="mb-2.5 flex flex-wrap items-center justify-between gap-2">
                            <div className="flex items-center gap-1.5 text-xs font-bold text-slate-700 dark:text-slate-200">
                                <Calendar className="h-3.5 w-3.5 text-indigo-500" />
                                <span>Yayın Takvimi (İsteğe Bağlı)</span>
                            </div>
                            <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                                <button
                                    type="button"
                                    onClick={() => setFormData((prev) => ({ ...prev, publishStartAt: formatLocalForInput(new Date()) }))}
                                    className="rounded-lg border border-slate-200 bg-white px-2 py-0.5 font-semibold text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                                >
                                    Şimdi Başlat
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setQuickEndDate(7)}
                                    className="rounded-lg border border-slate-200 bg-white px-2 py-0.5 font-semibold text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                                >
                                    +7 Gün
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setQuickEndDate(30)}
                                    className="rounded-lg border border-slate-200 bg-white px-2 py-0.5 font-semibold text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                                >
                                    +30 Gün
                                </button>
                                {(formData.publishStartAt || formData.publishEndAt) && (
                                    <button
                                        type="button"
                                        onClick={() => setFormData((prev) => ({ ...prev, publishStartAt: '', publishEndAt: '' }))}
                                        className="rounded-lg border border-rose-200 bg-rose-50 px-2 py-0.5 font-semibold text-rose-600 hover:bg-rose-100 dark:border-rose-900/50 dark:bg-rose-950/40 dark:text-rose-300"
                                    >
                                        Süresiz Yap
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                            <div>
                                <label className="mb-1 block text-[11px] font-semibold text-slate-500 dark:text-slate-400">
                                    Yayın Başlangıç (Boş = Hemen)
                                </label>
                                <input
                                    type="datetime-local"
                                    name="publishStartAt"
                                    value={formData.publishStartAt}
                                    onChange={handleChange}
                                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-800 outline-none focus:border-indigo-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                                />
                            </div>
                            <div>
                                <label className="mb-1 block text-[11px] font-semibold text-slate-500 dark:text-slate-400">
                                    Yayın Bitiş (Boş = Süresiz)
                                </label>
                                <input
                                    type="datetime-local"
                                    name="publishEndAt"
                                    value={formData.publishEndAt}
                                    onChange={handleChange}
                                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-800 outline-none focus:border-indigo-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Hedef Kitle Seçimi */}
                    <div className="space-y-2.5">
                        <label className="block text-xs font-bold text-slate-700 dark:text-slate-200">
                            Hedef Kitle
                        </label>
                        <div className="grid grid-cols-1 gap-2.5 md:grid-cols-3">
                            {TARGET_OPTIONS.map((opt) => {
                                const Icon = opt.icon;
                                const active = targetTypeNum === opt.value;
                                return (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        onClick={() => {
                                            setTargetSearch('');
                                            setFormData((prev) => ({ ...prev, targetType: opt.value }));
                                        }}
                                        className={`flex items-start gap-2.5 rounded-xl border p-3 text-left transition ${
                                            active
                                                ? 'border-indigo-600 bg-indigo-50/70 ring-1 ring-indigo-600 dark:border-indigo-400 dark:bg-indigo-950/50'
                                                : 'border-slate-200 bg-white hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800/80'
                                        }`}
                                    >
                                        <div
                                            className={`mt-0.5 rounded-lg p-1.5 ${
                                                active
                                                    ? 'bg-indigo-600 text-white'
                                                    : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                                            }`}
                                        >
                                            <Icon className="h-4 w-4" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="text-xs font-bold text-slate-900 dark:text-white">{opt.label}</div>
                                            <div className="mt-0.5 text-[11px] leading-tight text-slate-500 dark:text-slate-400">
                                                {opt.desc}
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Çoklu Departman veya Rol Seçici (Ctrl gerektirmeyen modern Checkbox/Chip yapısı) */}
                        {(targetTypeNum === 2 || targetTypeNum === 3) && (
                            <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-3.5 dark:border-slate-700 dark:bg-slate-900/60">
                                <div className="mb-2.5 flex flex-wrap items-center justify-between gap-2">
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs font-bold text-slate-700 dark:text-slate-200">
                                            {targetTypeNum === 2 ? 'Hedef Departmanları Seçin' : 'Hedef Rolleri Seçin'}
                                        </span>
                                        <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-bold text-indigo-700 dark:bg-indigo-900/60 dark:text-indigo-300">
                                            {targetTypeNum === 2 ? formData.departmentIds.length : formData.roleIds.length} seçili
                                        </span>
                                    </div>

                                    <div className="flex items-center gap-2 text-xs">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (targetTypeNum === 2) {
                                                    setFormData((prev) => ({
                                                        ...prev,
                                                        departmentIds: departments.map((d) => Number(d.id))
                                                    }));
                                                } else {
                                                    setFormData((prev) => ({
                                                        ...prev,
                                                        roleIds: roles.map((r) => Number(r.id))
                                                    }));
                                                }
                                            }}
                                            className="font-semibold text-indigo-600 hover:underline dark:text-indigo-400"
                                        >
                                            Tümünü Seç
                                        </button>
                                        <span className="text-slate-300 dark:text-slate-700">|</span>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (targetTypeNum === 2) {
                                                    setFormData((prev) => ({ ...prev, departmentIds: [] }));
                                                } else {
                                                    setFormData((prev) => ({ ...prev, roleIds: [] }));
                                                }
                                            }}
                                            className="font-semibold text-slate-500 hover:text-rose-600 dark:text-slate-400"
                                        >
                                            Temizle
                                        </button>
                                    </div>
                                </div>

                                <div className="mb-2.5 flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-2.5 py-1.5 dark:border-slate-700 dark:bg-slate-800">
                                    <Search className="h-3.5 w-3.5 text-slate-400" />
                                    <input
                                        type="text"
                                        value={targetSearch}
                                        onChange={(e) => setTargetSearch(e.target.value)}
                                        placeholder={targetTypeNum === 2 ? 'Departman ara...' : 'Rol ara...'}
                                        className="w-full bg-transparent text-xs text-slate-700 outline-none dark:text-slate-200"
                                    />
                                </div>

                                {loadingTargets ? (
                                    <div className="py-6 text-center text-xs text-slate-400">Hedef listesi yükleniyor...</div>
                                ) : (
                                    <div className="grid max-h-44 grid-cols-1 gap-1.5 overflow-y-auto pr-1 sm:grid-cols-2">
                                        {(targetTypeNum === 2 ? filteredDepartments : filteredRoles).map((item) => {
                                            const listKey = targetTypeNum === 2 ? 'departmentIds' : 'roleIds';
                                            const checked = (formData[listKey] || []).includes(Number(item.id));
                                            return (
                                                <button
                                                    key={item.id}
                                                    type="button"
                                                    onClick={() => toggleTargetItem(listKey, item.id)}
                                                    className={`flex items-center justify-between rounded-xl border px-3 py-2 text-left text-xs font-medium transition ${
                                                        checked
                                                            ? 'border-indigo-500 bg-indigo-50/90 text-indigo-900 dark:border-indigo-500/80 dark:bg-indigo-950/60 dark:text-indigo-200'
                                                            : 'border-slate-200/80 bg-white text-slate-700 hover:border-slate-300 dark:border-slate-700/80 dark:bg-slate-800 dark:text-slate-200'
                                                    }`}
                                                >
                                                    <span className="truncate">{item.name}</span>
                                                    <span
                                                        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                                                            checked
                                                                ? 'border-indigo-600 bg-indigo-600 text-white'
                                                                : 'border-slate-300 dark:border-slate-600'
                                                        }`}
                                                    >
                                                        {checked && <Check className="h-3 w-3" />}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Aksiyon Butonu & Görünüm Ayarları */}
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div className="space-y-1">
                            <label className="block text-xs font-bold text-slate-700 dark:text-slate-200">
                                Yönlendirme Buton Metni (İsteğe Bağlı)
                            </label>
                            <input
                                type="text"
                                name="actionButtonText"
                                maxLength={50}
                                value={formData.actionButtonText}
                                onChange={handleChange}
                                placeholder="Örn: Dokümanı İncele"
                                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-800 outline-none focus:border-indigo-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                            />
                        </div>
                        <div className="space-y-1">
                            <label className="block text-xs font-bold text-slate-700 dark:text-slate-200">
                                Yönlendirme Bağlantısı (URL)
                            </label>
                            <input
                                type="text"
                                name="actionUrl"
                                maxLength={500}
                                value={formData.actionUrl}
                                onChange={handleChange}
                                placeholder="https://..."
                                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-800 outline-none focus:border-indigo-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                            />
                        </div>
                    </div>

                    {/* Sabitleme ve Dashboard Seçenekleri */}
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <label
                            className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 transition ${
                                formData.pinned
                                    ? 'border-amber-300 bg-amber-50/60 dark:border-amber-800/70 dark:bg-amber-950/30'
                                    : 'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800/60'
                            }`}
                        >
                            <input
                                type="checkbox"
                                name="pinned"
                                checked={formData.pinned}
                                onChange={handleChange}
                                className="h-4 w-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                            />
                            <div className="min-w-0">
                                <div className="flex items-center gap-1.5 text-xs font-bold text-slate-800 dark:text-slate-100">
                                    <Pin className="h-3.5 w-3.5 text-amber-500" />
                                    <span>En Üste Sabitle (Pinned)</span>
                                </div>
                                <div className="text-[11px] text-slate-500 dark:text-slate-400">
                                    Duyuru panosunda her zaman en üstte vurgulu gösterilir.
                                </div>
                            </div>
                        </label>

                        <label
                            className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 transition ${
                                formData.showOnDashboard
                                    ? 'border-indigo-300 bg-indigo-50/50 dark:border-indigo-800/70 dark:bg-indigo-950/30'
                                    : 'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800/60'
                            }`}
                        >
                            <input
                                type="checkbox"
                                name="showOnDashboard"
                                checked={formData.showOnDashboard}
                                onChange={handleChange}
                                className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                            />
                            <div className="min-w-0">
                                <div className="flex items-center gap-1.5 text-xs font-bold text-slate-800 dark:text-slate-100">
                                    <LayoutDashboard className="h-3.5 w-3.5 text-indigo-500" />
                                    <span>Dashboard & Mobil Akışta Göster</span>
                                </div>
                                <div className="text-[11px] text-slate-500 dark:text-slate-400">
                                    Ana sayfa duyuru bileşeninde ve mobil listede yer alır.
                                </div>
                            </div>
                        </label>
                    </div>
                </>
            )}

            {/* Alt Butonlar */}
            <div className="flex items-center justify-end gap-2.5 border-t border-slate-200 pt-4 dark:border-slate-800">
                <button
                    type="button"
                    onClick={onCancel}
                    disabled={submitting}
                    className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                >
                    Vazgeç
                </button>
                <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-indigo-600 px-5 py-2 text-xs font-bold text-white shadow-sm transition hover:bg-indigo-700 disabled:opacity-50"
                >
                    {submitting ? 'Kaydediliyor...' : isNew ? 'Duyuruyu Kaydet' : 'Değişiklikleri Kaydet'}
                </button>
            </div>
        </form>
    );
};

export default AnnouncementForm;
